import { createSelector } from '@reduxjs/toolkit';
import { Visit } from './interfaces';
import { RootState, selectIsProviderAdmin, VisitRecordingStat } from '../../';
import { NOTE_STATUS } from '@ax/constants';
import { VisitStatusHistory } from '../visits/interfaces/visit-status-history-interface.ts';

export const totalVisitsSelector = createSelector(
  (state: RootState) => state.visits.items,
  (items: Visit[]) => items?.length ?? 0,
);

export const hasRecordingsSelector = (noteId: string | null) =>
  createSelector(
    (state: RootState) => state?.visits?.recordings,
    (recordings: Record<string, VisitRecordingStat[]>) =>
      !!noteId && !!recordings && !!recordings[noteId] && recordings[noteId].length > 0,
  );

export const totalVisitsWithRecordingsCountSelector = createSelector(
  (state: RootState) => state.visits.recordings,
  (recordings: Record<string, VisitRecordingStat[]>) =>
    Object.values(recordings ?? {}).filter((recording) => recording.length > 0).length,
);

export const statusHistoryByIdSelector = (noteId: string) =>
  createSelector(
    (state: RootState) => state.visits.items,
    (visits) => {
      const visit = visits.find((visit: Visit) => visit.uuid === noteId);

      return (visit?.statusHistory || [])
        .filter(
          (history: VisitStatusHistory) =>
            history.status === NOTE_STATUS.NOTE_UPLOADED_TO_EHR ||
            history.status === NOTE_STATUS.NOTE_UPLOADED_TO_MDS,
        )
        .sort((a: VisitStatusHistory, b: VisitStatusHistory) =>
          a.createdAt < b.createdAt ? -1 : 1,
        );
    },
  );

export const statusHistorySelector = createSelector(
  (state: RootState) => {
    const isAdmin = selectIsProviderAdmin(state);
    const statusHistory = statusHistoryByIdSelector(state.selectedVisit?.uuid)(state);

    return {
      statusHistory,
      isAdmin,
    } as { statusHistory: VisitStatusHistory[]; isAdmin: boolean };
  },
  ({ statusHistory, isAdmin }) => {
    return (statusHistory ?? []).slice(isAdmin ? -2 : -1);
  },
);

export const selectCurrentVisitStatus = (noteId: string) =>
  createSelector(
    (state: RootState) => state.visits.items,
    (visits) => {
      const visit = visits?.find((visit: Visit) => visit.uuid === noteId);

      if (visit?.isProcessing) {
        return NOTE_STATUS.CONTENT_PROCESSING;
      }

      return visit?.noteStatus;
    },
  );

export const selectVisitRecordings = (noteId: string) =>
  createSelector(
    (state: RootState) => state.visits?.recordings,
    (recordings) => recordings?.[noteId] ?? [],
  );
